import { UISwitch } from "common/components";
import { useDispatch, useSelector } from "common/utilities/react-redux";
import { useTranslate } from "common/utilities/useTranslate";
import { getIsAuthenticated } from "domains/authentication/selectors";
import * as Styled from "domains/search/components/SearchFilter/SearchFilter.styled";
import { SourcesFilter } from "domains/search/components/SourcesFilter/SourcesFilter";
import { searchSlice } from "domains/search/search.slice";
import { getCanAccessCreate, getCanSeeSectionContent } from "entities/permissions/selectors";

type SearchVisibilitySectionType =
  | "topics"
  | "keyIssues"
  | "publications"
  | "emergingThemes"
  | "videos"
  | "interactiveData"
  | "events"
  | "initiatives"
  | "stakeholders";

export const SearchFilter = () => {
  const dispatch = useDispatch();
  const translate = useTranslate();
  const isAuthenticated = useSelector(getIsAuthenticated);

  const searchResultsVisibility = useSelector(searchSlice.selectors.getSearchResultsVisibility);
  const accessToKeyIssueList = useSelector(getCanAccessCreate);
  const accessToTabContent = useSelector(getCanSeeSectionContent);

  const {
    setTopicsVisibility,
    setKeyIssuesVisibility,
    setPublicationsVisibility,
    setVideosVisibility,
    setInteractiveDataVisibility,
  } = searchSlice.actions;

  const accessRights: Partial<Record<SearchVisibilitySectionType, boolean>> = {
    topics: true,
    keyIssues: accessToKeyIssueList,
    publications: accessToTabContent,
    videos: accessToTabContent,
    interactiveData: accessToTabContent,
  };

  return (
    <Styled.FiltersContainer>
      <div>
        <Styled.Item hasAccess={accessRights.topics}>
          <UISwitch
            aria-label={translate("app.discover.topics.title")}
            isOn={!!accessRights.topics && searchResultsVisibility.areTopicsVisible}
            onClick={() => dispatch(setTopicsVisibility({ isVisible: !searchResultsVisibility.areTopicsVisible }))}
            isDisabled={!accessRights.topics}
          >
            {translate("app.discover.topics.title")}
          </UISwitch>
        </Styled.Item>
        <Styled.Item hasAccess={accessRights.keyIssues}>
          <UISwitch
            aria-label={translate("app.topic.key_issues")}
            isOn={!!accessRights.keyIssues && searchResultsVisibility.areKeyIssuesVisible}
            onClick={() =>
              dispatch(setKeyIssuesVisibility({ isVisible: !searchResultsVisibility.areKeyIssuesVisible }))
            }
            isDisabled={!accessRights.keyIssues}
          >
            {translate("app.topic.key_issues")}
          </UISwitch>
        </Styled.Item>
        <Styled.Item hasAccess={accessRights.publications}>
          <UISwitch
            aria-label={translate("app.discover.topic.tabs.knowledge.title")}
            isOn={!!accessRights.publications && searchResultsVisibility.arePublicationsVisible}
            onClick={() =>
              dispatch(setPublicationsVisibility({ isVisible: !searchResultsVisibility.arePublicationsVisible }))
            }
            isDisabled={!accessRights.publications}
          >
            {translate("app.discover.topic.tabs.knowledge.title")}
          </UISwitch>
        </Styled.Item>
        <Styled.Item hasAccess={accessRights.videos}>
          <UISwitch
            aria-label={translate("app.discover.topic.tabs.videos.title")}
            isOn={!!accessRights.videos && searchResultsVisibility.areVideosVisible}
            onClick={() => dispatch(setVideosVisibility({ isVisible: !searchResultsVisibility.areVideosVisible }))}
            isDisabled={!accessRights.videos}
          >
            {translate("app.discover.topic.tabs.videos.title")}
          </UISwitch>
        </Styled.Item>
        <Styled.Item hasAccess={accessRights.interactiveData}>
          <UISwitch
            aria-label={translate("app.discover.topic.tabs.interactive_data.title")}
            isOn={!!accessRights.interactiveData && searchResultsVisibility.isInteractiveDataVisible}
            onClick={() =>
              dispatch(setInteractiveDataVisibility({ isVisible: !searchResultsVisibility.isInteractiveDataVisible }))
            }
            isDisabled={!accessRights.interactiveData}
          >
            {translate("app.discover.topic.tabs.interactive_data.title")}
          </UISwitch>
        </Styled.Item>
      </div>

      {isAuthenticated && (
        <>
          <Styled.Divider />
          <SourcesFilter />
        </>
      )}
    </Styled.FiltersContainer>
  );
};
